<template>
  <div>
    <template v-if="withCustomArea">
      <slot name="label">
        <ui-label
          v-if="label"
          :plan-limits="planLimits"
          :for="name"
          :text="label"
          :help-tooltip="labelHelp"
        />
      </slot>
      <div class="btn-group">
        <ui-button
          class="btn-list"
          icon="fas fa-list"
          :text="t('From List')"
          :button-style="activeComponent === 'list' ? 'accent' : 'default'"
          @click="changeTab('list')"
        />
        <ui-button
          class="btn-custom"
          icon="fas fa-pen"
          :text="t('Custom')"
          :button-style="activeComponent === 'custom' ? 'accent' : 'default'"
          @click="changeTab('custom')"
        />
      </div>
      <div v-if="activeComponent === 'list'">
        <div class="ui-select__wrapper">
          <slot name="label">
            <ui-label
              v-if="label"
              :plan-limits="planLimits"
              :for="name"
              :text="label"
              :help-tooltip="labelHelp"
            />
          </slot>
          <component
            :is="selectComponent"
            ref="selectRef"
            :class="selectClasses"
            :items="calculatedItems"
            :placeholder="placeholder"
            :item-title="resolvedItemTitleKey"
            :item-value="itemValue"
            :model-value="resolvedModelValue"
            :autocomplete="autocompleteAttr"
            :error-messages="errorMessages"
            :return-object="returnObject"
            :menu-icon="menuIconForField"
            :multiple="multiple"
            :disabled="disabled"
            :readonly="showPlanLimitsIcon"
            :prepend-inner-icon="pPrependInnerIcon"
            :prepend-icon="pPrependIcon"
            :hide-no-data="computedLoading"
            :chips="chips"
            :attach="attach"
            :search-input="searchInput"
            :no-filter="noFilterComputed"
            :menu-props="menuPropsComputed"
            @update:model-value="onInput"
            @keyup="emit('keyup', $event)"
            @update:focused="onUpdateFocused"
            @focus="onFocus"
            @blur="onBlur"
            @change="onChange"
            @update:search="onUpdateSearchInput"
            @mousedown="onMousedown"
          >
            <template v-if="$slots.item" #item="slotProps">
              <slot name="item" v-bind="slotProps" />
            </template>
            <template v-else #item="slotProps">
              <v-list-item
                v-bind="listItemPropsForCustomContent(slotProps.props)"
                :link="false"
                :title="itemRowLabel(slotProps)"
                @click.stop="onAutocompleteListItemClick(slotProps, $event)"
              >
                <template v-if="isMultiple" #prepend>
                  <div class="ui-select__item-checkbox">
                    <div class="v-input v-input--selection-controls v-input--checkbox theme--light">
                      <div class="v-input__control">
                        <div class="v-input__slot">
                          <div class="v-input--selection-controls__input">
                            <i
                              class="ui-select__item-checkbox-unchecked v-icon far fa-square theme--light"
                              aria-hidden="true"
                            />
                            <i
                              class="ui-select__item-checkbox-checked v-icon fas fa-check-square theme--light"
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </v-list-item>
            </template>
            <template v-if="$slots.chip || ($slots.selection && chips)" #chip="slotProps">
              <slot v-if="$slots.chip" name="chip" v-bind="slotProps" />
              <slot v-else name="selection" v-bind="slotProps" />
            </template>
            <template v-if="$slots.selection" #selection="props">
              <slot name="selection" v-bind="props" />
            </template>
            <template v-if="computedLoading || $slots.append" #append-inner>
              <span v-if="computedLoading" class="ui-select__loader-menu-icon" aria-hidden="true">
                <LoadingCircle color="black" />
              </span>
              <slot v-if="$slots.append" name="append" />
            </template>
            <template v-if="$slots['no-data']" #no-data>
              <slot name="no-data" />
            </template>
          </component>
        </div>
      </div>
      <div v-else-if="activeComponent === 'custom'" class="custom-field-wrapper">
        <ui-info v-if="customInfo" class="mb-8">{{ customInfo }}</ui-info>
        <v-textarea
          v-model="customTextareaLocal"
          class="custom-field"
          variant="plain"
          :placeholder="placeholder"
          :rows="10"
          :hide-details="true"
          @blur="onTextareaBlur"
        />
      </div>
    </template>
    <template v-else>
      <div class="ui-select__wrapper">
        <slot name="label">
          <ui-label
            v-if="label"
            :plan-limits="planLimits"
            :for="name"
            :text="label"
            :help-tooltip="labelHelp"
          />
        </slot>
        <component
          :is="selectComponent"
          ref="selectRef"
          :class="selectClasses"
          :items="calculatedItems"
          :placeholder="placeholder"
          :item-title="resolvedItemTitleKey"
          :item-value="itemValue"
          :model-value="resolvedModelValue"
          :autocomplete="autocompleteAttr"
          :error-messages="errorMessages"
          :return-object="returnObject"
          :menu-icon="menuIconForField"
          :multiple="multiple"
          :disabled="disabled"
          :readonly="showPlanLimitsIcon"
          :prepend-inner-icon="pPrependInnerIcon"
          :prepend-icon="pPrependIcon"
          :hide-no-data="computedLoading"
          :chips="chips"
          :attach="attach"
          :search-input="searchInput"
          :no-filter="noFilterComputed"
          :menu-props="menuPropsComputed"
          @update:model-value="onInput"
          @keyup="emit('keyup', $event)"
          @update:focused="onUpdateFocused"
          @focus="onFocus"
          @blur="onBlur"
          @change="onChange"
          @update:search="onUpdateSearchInput"
          @mousedown="onMousedown"
        >
          <template v-if="$slots.item" #item="slotProps">
            <slot name="item" v-bind="slotProps" />
          </template>
          <template v-else #item="slotProps">
            <v-list-item
              v-bind="listItemPropsForCustomContent(slotProps.props)"
              :link="false"
              :title="itemRowLabel(slotProps)"
              @click.stop="onAutocompleteListItemClick(slotProps, $event)"
            >
              <template v-if="isMultiple" #prepend>
                <div class="ui-select__item-checkbox">
                  <div class="v-input v-input--selection-controls v-input--checkbox theme--light">
                    <div class="v-input__control">
                      <div class="v-input__slot">
                        <div class="v-input--selection-controls__input">
                          <i
                            class="ui-select__item-checkbox-unchecked v-icon far fa-square theme--light"
                            aria-hidden="true"
                          />
                          <i
                            class="ui-select__item-checkbox-checked v-icon fas fa-check-square theme--light"
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </v-list-item>
          </template>
          <template v-if="$slots.chip || ($slots.selection && chips)" #chip="slotProps">
            <slot v-if="$slots.chip" name="chip" v-bind="slotProps" />
            <slot v-else name="selection" v-bind="slotProps" />
          </template>
          <template v-if="$slots.selection" #selection="props">
            <slot name="selection" v-bind="props" />
          </template>
          <template v-if="computedLoading || $slots.append" #append-inner>
            <span v-if="computedLoading" class="ui-select__loader-menu-icon" aria-hidden="true">
              <LoadingCircle color="black" />
            </span>
            <slot v-if="$slots.append" name="append" />
          </template>
          <template v-if="$slots['no-data']" #no-data>
            <slot name="no-data" />
          </template>
        </component>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  useSlots,
} from 'vue';
import { debounce, throttle } from 'lodash';
import { usePlanLimits, useI18n } from '@/composables';
import LoadingCircle from '@/components/global-loaders/circle.vue';
import { $emitter } from '@/services/emitter';
import helpersService from '@/services/helpers';

const props = withDefaults(
  defineProps<{
    items?: any;
    withCustomArea?: boolean;
    placeholder?: string;
    customInfo?: string;
    label?: string;
    labelHelp?: string;
    name?: string;
    itemText?: string;
    itemValue?: string;
    itemTitle?: string;
    modelValue?: any;
    value?: any;
    searchInput?: any;
    autocomplete?: boolean;
    errorMessages?: string[] | string;
    planLimits?: keyof BillingLimits;
    returnObject?: boolean;
    multiple?: boolean | string;
    buttonStyle?: string;
    mini?: boolean | string;
    disabled?: boolean;
    prependIcon?: string;
    prependInnerIcon?: string;
    combobox?: boolean | string;
    chips?: boolean;
    menuProps?: Record<string, any>;
    hideDetails?: number;
    maxWidthCalc?: boolean | string;
    dialogRepositionParam?: string;
    menuContentClass?: string;
    attach?: any;
    multiLine?: boolean | string;
    fetchOptions?: (opts: { search: string }) => Promise<any[]>;
    loading?: any;
  }>(),
  {
    withCustomArea: false,
    itemText: 'text',
    itemValue: 'value',
    autocomplete: true,
    buttonStyle: 'default',
    menuProps: () => ({}),
  },
);

const emit = defineEmits<{
  input: [value: any];
  'update:modelValue': [value: any];
  keyup: [event: any];
  focus: [event: any];
  blur: [event: any, searchValue?: string];
  change: [value: any];
  'update:searchInput': [value: any];
  mousedown: [event: any];
  'blur:textarea': [event: any, payload: { value: string[] }];
}>();

const slots = useSlots();
const { t } = useI18n();
const { showPlanLimitsIcon, handleClickPlanLimits } = usePlanLimits(
  () => props.planLimits,
  false,
);

const isMultiple = computed(() => props.multiple || props.multiple === '');

// Prefer itemTitle (Vuetify / Vue 3 callers) over itemText (legacy item-text)
const resolvedItemTitleKey = computed(
  () => props.itemTitle || props.itemText || 'text',
);

function findItemByValue(id: unknown): Record<string, any> | undefined {
  if (id === undefined || id === null || id === '') return undefined;
  const key = props.itemValue ?? 'value';
  const pools = [...(props.items || []), ...fetchedItems.value];
  return pools.find((item: any) => item?.[key] === id);
}

function resolveReturnObjectValue(raw: any): any {
  if (raw === undefined || raw === null) return raw;
  if (Array.isArray(raw)) {
    return raw.map((item) => resolveReturnObjectValue(item));
  }
  if (typeof raw !== 'object') return raw;

  const textKey = resolvedItemTitleKey.value;
  const valueKey = props.itemValue ?? 'value';
  const display = raw[textKey];
  const id = raw[valueKey];

  // Empty object (e.g. { id: '', name: '' }) — treat as no value so placeholder shows
  if (
    (id === undefined || id === null || id === '')
    && (display == null || String(display).length === 0)
  ) {
    return null;
  }

  if (display != null && String(display).length > 0) {
    return raw;
  }

  const full = findItemByValue(id);
  if (full) {
    return { ...full, ...raw };
  }

  return raw;
}

function unwrapToItemValue(raw: any): any {
  if (raw === undefined || raw === null) return raw;
  const key = props.itemValue ?? 'value';
  if (isMultiple.value && Array.isArray(raw)) {
    return raw.map(item => {
      if (
        item !== null &&
        typeof item === 'object' &&
        !Array.isArray(item) &&
        key in item
      ) {
        return (item as Record<string, any>)[key];
      }
      return item;
    });
  }
  if (typeof raw === 'object' && !Array.isArray(raw) && key in raw) {
    return (raw as Record<string, any>)[key];
  }
  return raw;
}

const resolvedModelValue = computed(() => {
  const raw = props.modelValue !== undefined ? props.modelValue : props.value;
  // Vuetify 3 treats '' as a selected value and hides the placeholder.
  if (raw === '') return null;
  if (props.returnObject) {
    return resolveReturnObjectValue(raw);
  }
  const unwrapped = unwrapToItemValue(raw);
  return unwrapped === '' ? null : unwrapped;
});

const selectRef = ref();
const width = ref(0);
const isMenuBottom = ref(true);
const loadingInner = ref(false);
const activeComponent = ref('list');
const isFocused = ref(false);
const isBlurCalled = ref(false);
const isSearchUpdateFrozenAfterClick = ref(false);
const searchValue = ref('');
const fetchedItems = ref<any[]>([]);
const customTextareaLocal = ref('');

const itemObject = computed(() => {
  if (!props.fetchOptions || !props.items) return {};
  return (
    props.items.reduce((acc: Record<string, any>, item: any) => {
      if (item?.[props.itemValue!]) {
        acc[item[props.itemValue!]] = item;
      }
      return acc;
    }, {}) || {}
  );
});

const calculatedItems = computed(() => {
  if (props.fetchOptions && searchValue.value) {
    return fetchedItems.value;
  }
  const itemVal = resolvedModelValue.value?.[props.itemValue!];
  if (props.fetchOptions && itemVal && !itemObject.value[itemVal]) {
    return [resolvedModelValue.value, ...(props.items || [])];
  }
  return props.items || [];
});

const fetchOptionsSearch = () => {
  loadingInner.value = false;
  if (!searchValue.value || !props.fetchOptions) return;
  loadingInner.value = true;
  const request = props.fetchOptions({ search: searchValue.value });
  if (!request?.then) {
    loadingInner.value = false;
    return;
  }
  return request.then((data: any) => {
    if (searchValue.value) fetchedItems.value = data;
    loadingInner.value = false;
  });
};

const fetchOptionsDebounced = debounce(fetchOptionsSearch, 500);

watch(searchValue, () => {
  if (props.fetchOptions && !!searchValue.value) {
    loadingInner.value = true;
    fetchOptionsDebounced();
  }
});

const selectComponent = computed(() =>
  props.combobox || props.combobox === ''
    ? 'v-combobox'
    : props.autocomplete
      ? 'v-autocomplete'
      : 'v-select',
);

// Vuetify expects string for autocomplete (HTML attribute), not boolean
const autocompleteAttr = computed(() => {
  const a = props.autocomplete;
  if (typeof a === 'string') return a;
  return undefined;
});

const noFilterComputed = computed(() => !!props.fetchOptions);

function slotItemRaw(slot: { item: any; internalItem?: { raw?: any } }) {
  return slot.internalItem?.raw ?? slot.item?.raw ?? slot.item;
}

const itemSlotLabel = (slot: { item: any; internalItem?: { raw?: any } }, textKey: string) => {
  const raw = slotItemRaw(slot);
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    const value = (raw as Record<string, unknown>)[textKey];
    if (value != null && String(value).length > 0) return String(value);
  }
  const internal = slot.item;
  if (internal != null && typeof internal === 'object' && 'title' in internal) {
    const title = (internal as { title: unknown }).title;
    if (title != null && String(title).length > 0) return String(title);
  }
  return '';
};

const itemRowLabel = (slot: { item: any; internalItem?: { raw?: any } }) => {
  const titleKey = props.itemTitle;
  const textKey = props.itemText;
  if (titleKey && textKey && titleKey !== textKey) {
    const labelTitle = itemSlotLabel(slot, titleKey);
    const labelText = itemSlotLabel(slot, textKey);
    if (labelTitle && labelText) {
      return labelTitle !== labelText ? `${labelTitle} — ${labelText}` : labelText;
    }
    return labelText || labelTitle;
  }
  return itemSlotLabel(slot, (textKey || titleKey)!);
};

/** Vuetify puts `title` on list item props; we render label in the slot — drop title or it duplicates. */
const listItemPropsForCustomContent = (itemProps: Record<string, unknown>) => {
  const { title: _title, subtitle: _subtitle, onClick: _onClick, ...rest } = itemProps;
  return rest;
};

type AutocompleteItemSlot = {
  item: unknown;
  index: number;
  props: Record<string, unknown>;
};

const listItemActivateDepth = ref(0);

/**
 * VAutocomplete puts `select(item)` on `props.onClick`; VListItem never calls that prop from its DOM handler.
 * With `:link="false"` we rely on this `@click` only. Guard against duplicate invocations (double `select` toggles
 * multi-value back, e.g. removes Master when adding another workspace).
 */
const onAutocompleteListItemClick = (slot: AutocompleteItemSlot, event: MouseEvent) => {
  if (slot.props.disabled) return;
  if (listItemActivateDepth.value > 0) return;
  event.stopPropagation();
  const handler = slot.props.onClick;
  if (handler == null) return;
  listItemActivateDepth.value++;
  try {
    if (typeof handler === 'function') {
      handler(event);
      return;
    }
    if (Array.isArray(handler)) {
      for (const fn of handler) {
        if (typeof fn === 'function') {
          (fn as (event: MouseEvent) => void)(event);
        }
      }
    }
  } finally {
    listItemActivateDepth.value--;
  }
};

const isMenuOpen = ref(false);

const chevronIcon = computed(() =>
  isMenuOpen.value ? 'far fa-angle-up' : 'far fa-angle-down',
);

const iconPrefix = 'ui-select__icon';
const pPrependIcon = computed(() =>
  props.prependIcon ? `${props.prependIcon} ${iconPrefix}` : props.prependIcon,
);
const pPrependInnerIcon = computed(() =>
  props.prependInnerIcon
    ? `${props.prependInnerIcon} ${iconPrefix}`
    : props.prependInnerIcon,
);

const styles = ['default', 'text'];
const pButtonStyle = computed(() =>
  styles.includes(props.buttonStyle!) ? props.buttonStyle : styles[0],
);

const isAttach = computed(() => typeof props.attach !== 'undefined');

const computedLoading = computed(() => !!(loadingInner.value || props.loading));

const menuIconForField = computed(() => {
  if (computedLoading.value || slots.append) return false;
  return chevronIcon.value;
});

const menuContentClasses = computed(() => {
  const result = ['ui-select__menu'];
  if (props.menuContentClass) result.push(props.menuContentClass);
  result.push(isMenuBottom.value ? 'ui-select__menu--bottom' : 'ui-select__menu--top');
  if (!isAttach.value && (props.multiLine || props.multiLine === '')) {
    result.push('ui-select__menu--multi-line');
  }
  return result.join(' ');
});

const normalizeContentClassParts = (classValue: unknown): string[] => {
  if (typeof classValue === 'string') {
    return classValue.split(/\s+/).filter(Boolean);
  }
  if (Array.isArray(classValue)) {
    return classValue.flatMap(normalizeContentClassParts);
  }
  return [];
};

const menuPropsComputed = computed(() => {
  const passedMenuProps = props.menuProps ?? {};
  const mergedContentClass = normalizeContentClassParts(menuContentClasses.value)
    .concat(normalizeContentClassParts(passedMenuProps.contentClass))
    .join(' ');

  return {
    transition: false,
    ripple: false,
    bottom: true,
    offsetY: true,
    maxWidth: width.value ? width.value : 'auto',
    ...passedMenuProps,
    contentClass: mergedContentClass,
  };
});

const selectClasses = computed(() => ({
  'ui-select': true,
  'ui-select--default': pButtonStyle.value === 'default',
  'ui-select--text': pButtonStyle.value === 'text',
  'ui-select--mini': props.mini,
  'ui-select--prepend-icon': !!(props.prependIcon || props.prependInnerIcon),
  'ui-select--without-prepend-icon': !props.prependIcon && !props.prependInnerIcon,
  'ui-select--hide-input': !props.autocomplete,
  'ui-select--controls-no-hide-details': props.hideDetails === 0,
  'ui-select--controls-hide-details': (props.hideDetails ?? 0) > 0,
  'ui-select--controls-hide-details-compensate': props.hideDetails === 2,
  'ui-select--has-items': props.items && props.items.length,
  'ui-select--chevron': !slots.append && !slots['append-inner'],
  'ui-select--menu-bottom': isMenuBottom.value,
  'ui-select--menu-top': !isMenuBottom.value,
  'ui-select--attach-select-top': props.attach === '',
  'ui-select--attach-select-bottom': props.attach === null,
  'ui-select--multi-line': props.multiLine || props.multiLine === '',
  'ui-controll-fix': true,
  'mt-4': props.label || !!slots['label'],
}));

const getCustomTextareaInitialValue = () => {
  let val = resolvedModelValue.value;
  if (props.returnObject && Array.isArray(val)) {
    const textKey = resolvedItemTitleKey.value;
    val = val.map((item: any) => item[textKey] ?? item);
  }
  return (val || []).join('\n');
};

watch(
  [() => activeComponent.value, () => resolvedModelValue.value],
  () => {
    if (activeComponent.value === 'custom') {
      customTextareaLocal.value = getCustomTextareaInitialValue();
    }
  },
  { immediate: true },
);

const updateIsMenuBottom = () => {
  if (!selectRef.value) return;
  const menu = (selectRef.value as any).$refs?.menu;
  if (!menu) return;
  const calculatedTop = parseInt(menu.calculatedTop, 10);
  if (isAttach.value || calculatedTop === -1) return;
  isMenuBottom.value = menu.dimensions?.activator?.top < calculatedTop;
};

const onWindowResize = () => {
  recalcMaxWidth();
  helpersService.afterBrowserRedraw(() => updateIsMenuBottom());
};

const onWindowResizeThrottled = throttle(onWindowResize, 500);

const onBusDialogScroll = (param: unknown) => {
  if (props.dialogRepositionParam && props.dialogRepositionParam === String(param)) {
    const select = selectRef.value as any;
    if (select?.isMenuActive) select.isMenuActive = false;
    setTimeout(() => updateIsMenuBottom(), 300);
  }
};

const recalcMaxWidth = () => {
  if (!selectRef.value) return;
  const rect = (selectRef.value as any).$el?.getBoundingClientRect?.();
  if (rect?.width) width.value = Math.floor(rect.width);
};

const changeTab = (name: string) => {
  activeComponent.value = name;
};

const onInput = ($event: any) => {
  isSearchUpdateFrozenAfterClick.value = true;
  setTimeout(() => {
    isSearchUpdateFrozenAfterClick.value = false;
  }, 300);
  emit('update:modelValue', $event);
  emit('input', $event);
  emit('change', $event);
};

const onUpdateFocused = (focused: boolean) => {
  if (focused) {
    isBlurCalled.value = false;
    isFocused.value = true;
    return;
  }
  isFocused.value = false;
  if (!searchValue.value) fetchedItems.value = [];
};

const onFocus = ($event: any) => {
  isBlurCalled.value = false;
  isFocused.value = true;
  emit('focus', $event);
};

const onBlur = ($event: any) => {
  const select = selectRef.value as any;
  isFocused.value = false;
  nextTick(() => {
    if (!isBlurCalled.value) {
      select?.blur?.();
      isBlurCalled.value = true;
    }
  });
  if (!searchValue.value) fetchedItems.value = [];
  emit('blur', $event, searchValue.value);
};

const onChange = ($event: any) => {
  if ($event instanceof Event) return;
  if (!isMultiple.value) {
    const select = selectRef.value as any;
    select?.blur?.();
    isBlurCalled.value = true;
  }
  emit('change', $event);
};

const onUpdateSearchInput = ($event: any) => {
  emit('update:searchInput', $event);
  if (!$event) {
    searchValue.value = $event;
    return;
  }
  if (!searchValue.value && resolvedModelValue.value?.[props.itemText!] === $event)
    return;
  if (isSearchUpdateFrozenAfterClick.value) return;
  searchValue.value = $event;
};

const onMousedown = ($event: any) => {
  if (showPlanLimitsIcon.value) return handleClickPlanLimits();
  isFocused.value = true;
  emit('mousedown', $event);
  if (!width.value) recalcMaxWidth();
};

const changeCustomField = (event: { target: { value: string } }) =>
  event.target.value.split('\n').filter((item: string) => !!item);

const onTextareaBlur = (event: { target: { value: string } }) => {
  emit('blur:textarea', event as any, { value: changeCustomField(event) });
};

onMounted(() => {
  recalcMaxWidth();
  window.addEventListener('resize', onWindowResizeThrottled);
  $emitter.on('dialog-scroll', onBusDialogScroll);

  watch(
    () => {
      const select = selectRef.value as any;
      return select ? [select.isMenuActive, select.virtualizedItems] : [];
    },
    val => {
      isMenuOpen.value = !!(val as any[])?.[0];
      nextTick(() => updateIsMenuBottom());
      setTimeout(() => updateIsMenuBottom(), 500);
    },
    { deep: true, immediate: true },
  );

  watch(
    () => (selectRef.value as any)?.isMenuActive,
    () => {
      nextTick(() => recalcMaxWidth());
    },
  );
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResizeThrottled);
  $emitter.off('dialog-scroll', onBusDialogScroll);
});
</script>

<style lang="scss" scoped>
.btn-group {
  display: flex;
  margin-bottom: 8px;

  :deep(.btn-wrapper + .btn-wrapper) {
    margin-left: 0 !important;
  }
}

.btn-list :deep(.ui-button) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.btn-custom :deep(.ui-button) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>

<style lang="scss">
.custom-field {
  .v-field__input,
  textarea {
    background-color: var(--color-white);
  }
}

.ui-select__item--has-title {
  height: 32px;
}

.ui-select {
  padding-top: 0;
  margin-top: 0;
  position: relative;
  color: var(--color-graphite) !important;
  .v-icon {
    opacity: 1;
    font-size: 12px !important;
    line-height: 11px;
    color: inherit !important;
  }

  .v-field__prepend-inner .v-icon,
  .v-field__append-inner .v-icon,
  .v-input__icon .v-icon,
  .v-chip .v-icon {
    color: inherit !important;
  }
  .v-select__selections {
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
  }
  .v-select-list {
    padding: 0 !important;
  }

  .v-input__control {
    flex-grow: 1;
  }

  /* Vuetify 3: .v-field replaces .v-input__slot; hide default outline/overlay when we paint the field */
  &--default .v-field__outline,
  &--default .v-field__overlay,
  &--text .v-field__outline,
  &--text .v-field__overlay {
    display: none !important;
  }

  /* Let button/input gradients and borders on .v-field show through inner layers */
  &--default .v-field__field,
  &--text .v-field__field,
  &--default .v-field__input,
  &--text .v-field__input {
    background: transparent !important;
  }

  &.primary--text {
    color: var(--color-graphite) !important;
  }

  .v-field {
    --v-input-control-height: 32px;
    --v-field-padding-top: 0;
    --v-field-padding-bottom: 0;
    border-radius: 6px;
    margin-bottom: 0;
    color: inherit !important;
    height: 32px;
    min-height: 32px;
  }

  .v-field__input {
    min-height: 32px;
    min-width: 0;
    padding: 0 0 0 8px;
    opacity: 1;
  }

  &.v-select--chips,
  &.v-autocomplete--chips,
  &.v-combobox--chips {
    .v-field__input {
      gap: 0;
      column-gap: 0;
      row-gap: 0;
    }

    .v-chip {
      height: 16px !important;
      margin: 4px 2px !important;
      background-color: var(--color-green) !important;
      border-color: var(--color-green);
    }

    .v-chip.v-chip--disabled {
      opacity: 1 !important;
      background-color: var(--color-gray10) !important;
      border: 1px solid var(--color-gray20) !important;

      .v-chip__content {
        color: var(--color-black);
      }
    }
  }

  input {
    max-height: 32px;
  }

  &--multi-line.v-select.v-select--chips,
  &--multi-line.v-autocomplete.v-autocomplete--chips,
  &--multi-line.v-combobox.v-combobox--chips {
    height: auto;

    .v-chip {
      max-width: 100%;
      overflow: hidden;
    }

    .v-chip__content {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
    }

    .v-select__selections,
    .v-field__input {
      min-height: 32px;
      padding-top: 4px;
      padding-bottom: 4px;
    }

    .v-field {
      height: auto;
    }

    input {
      height: 32px;
      min-width: 60px;
      margin-top: -4px;
      margin-bottom: -4px;
    }
  }

  &--without-prepend-icon {
    .v-field__input {
      padding-left: 8px;
    }
  }

  @import '@/styles/ui/button-like-default.scss';
  @import '@/styles/ui/button-like-mini.scss';
  @import '@/styles/ui/input-like-default.scss';
  @import '@/styles/ui/input-general.scss';

  &--text {
    @include inputLikeDefaultIdle;

    .v-field {
      @include inputLikeDefaultBorderIdle;
    }

    &.v-input--focused .v-field {
      @include inputLikeDefaultBorderFocused;
    }

    &.error--text .v-field,
    &.v-input--error .v-field {
      @include inputLikeDefaultBorderError;
    }

    &.v-input--is-disabled {
      .v-field {
        @include inputLikeDefaultBorderDisabled;
      }

      input {
        @include inputLikeDefaultInputDisabled;
      }
    }
  }

  &--default {
    > .v-input__control > .v-field {
      @include buttonLikeDefaultIdle();
    }

    &:hover {
      > .v-input__control > .v-field {
        @include buttonLikeDefaultHover();
      }
    }

    &:active,
    &:focus {
      > .v-input__control > .v-field {
        @include buttonLikeDefaultFocus();
      }
    }

    &.v-input--focused.v-autocomplete,
    &.v-input--focused.v-combobox {
      > .v-input__control > .v-field {
        border-color: var(--color-green);
      }
    }

    &.error--text.ui-select,
    &.v-input--error.ui-select {
      > .v-input__control > .v-field {
        border-color: var(--color-red);
      }
    }

    &.v-input--is-disabled.ui-select {
      > .v-input__control > .v-field {
        border-color: var(--color-gray10);
      }
    }

    .v-input__prepend-inner,
    .v-field__prepend-inner {
      width: auto;
      height: auto;
      min-width: 0;
      padding-right: 4px;
      margin-top: 0;
      align-self: center;
      flex-shrink: 0;
    }

    .v-input__icon--prepend-inner,
    .v-field__prepend-inner .v-icon {
      min-width: 0;
      width: auto;
      padding-left: 8px;
    }
  }

  @at-root #{selector-append(& + '--default', & + '--disabled')} {
    .v-field {
      @include buttonLikeDefaultDisabled();
    }
  }

  &--text {
    .v-input__icon--prepend,
    .v-input__icon--prepend-inner,
    .v-field__prepend-inner > .v-icon {
      min-width: 12px;
      width: 0;
    }

    .v-input__prepend-inner,
    .v-field__prepend-inner {
      padding-left: 8px;
    }

    .v-input__prepend-inner,
    .v-field__prepend-inner {
      margin-right: auto;
      padding-right: 4px;
    }
  }

  &--text &__icon {
    font-size: 12px;
    line-height: 1.33;
    color: var(--color-gray40) !important;
  }

  &--text .v-field__prepend-inner .v-icon {
    color: var(--color-gray40) !important;
  }

  &.v-input--focused &__icon,
  &.v-input--focused .v-field__prepend-inner .v-icon {
    color: var(--color-graphite) !important;
  }

  @at-root #{selector-append(& + '--has-items', & + '--menu-bottom', '.v-select--is-menu-active')} {
    .v-field {
      border-radius: 6px 6px 0 0;
    }
  }

  @at-root #{selector-append(& + '--has-items', & + '--menu-top', '.v-select--is-menu-active')} {
    .v-field {
      border-radius: 0 0 6px 6px;
    }
  }

  @at-root #{selector-append(& + '--text', & + '--has-items')} {
    &.v-select--is-menu-active .v-field {
      border-radius: 6px 6px 0 0;
    }
  }

  &--hide-input {
    input {
      display: none;
    }

    .v-input__append-inner,
    .v-field__append-inner {
      padding-left: 0;
    }
  }

  &--prepend-icon {
    .v-field__input {
      padding-left: 0;
    }
  }

  .v-select__selection {
    font-size: 12px;
    line-height: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    white-space: nowrap;
  }

  .v-autocomplete__selection {
    font-size: 12px;
    line-height: 14px;
    min-width: 0;
    max-width: 100%;
  }

  .v-autocomplete__selection-text {
    font-size: 12px;
    line-height: 14px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &--mini {
    input {
      text-transform: none;
      font-size: 12px;
      line-height: 24px;
    }

    &.v-text-field .v-input__append-inner,
    &.v-text-field .v-field__append-inner {
      margin-top: 0;
    }

    .v-field {
      height: 24px;
      min-height: 24px;
    }

    .v-field__input {
      min-height: 24px;
    }
  }

  @at-root #{selector-append(& + '--hide-input', & + '--mini')} {
    .v-select__selection--comma {
      margin: 0;
    }
  }

  &--chevron {
    .v-input__icon--append,
    .v-field__append-inner .v-icon {
      width: auto;
      height: auto;
      min-width: 0;
      margin-right: 8px;
      margin-left: 4px;
    }

    .v-input__append-inner,
    .v-field__append-inner {
      margin-top: 0;
      align-self: center;
    }
  }

  .ui-select__loader-menu-icon {
    display: inline-flex;
    align-items: center;
    margin-inline-start: 4px;
    margin-inline-end: 8px;

    :deep(.circle-wrp) {
      margin: 0;
    }
  }
}

.ui-select__menu .v-card {
  border-radius: 0 !important;
}

.ui-select__menu--bottom {
  margin-top: -1px;
}
.ui-select__menu--bottom > .v-card,
.ui-select__menu--bottom > .v-sheet,
.ui-select__menu--bottom > .v-list {
  border-radius: 0 0 8px 8px !important;
  box-shadow: 0 4px 16px 0 var(--color-black20) !important;
}

.ui-select__menu--top.v-menu__content,
.ui-select__menu--top.v-overlay__content {
  border-radius: 8px 8px 0 0;
  box-shadow: 0 -4px 16px 0 var(--color-black20) !important;
}

.ui-select__menu.v-menu__content,
.ui-select__menu.v-overlay__content {
  background-color: var(--color-white);

  .v-card {
    border-radius: inherit;
  }

  .v-list {
    background-color: var(--color-white);
    padding: 4px 0 4px;
  }

  .v-list-item {
    display: flex;
    min-height: 32px;
    height: unset;
    align-items: center;
    font-size: 12px;
    padding-left: 8px;
    padding-right: 8px;
    cursor: pointer;
  }

  .v-list-item__content {
    padding-left: 0;
    padding-right: 0;
    min-width: 0;
  }

  .v-list-item__content .metrics-picker-item {
    width: 100%;
    min-width: 0;
  }

  .v-list-item__prepend {
    min-width: 24px;
    flex-shrink: 0;
    align-self: center;
  }

  .ui-select__item-checkbox {
    display: flex;
    align-items: center;

    .v-input--selection-controls__input {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 0;
      width: 24px !important;
      height: 24px !important;
      min-width: 24px;
      min-height: 24px;
      flex-shrink: 0;
    }
  }

  .ui-select__item-checkbox-unchecked {
    font-size: 12px;
    display: flex;
    color: var(--color-gray40) !important;
  }

  .ui-select__item-checkbox-checked {
    font-size: 12px;
    display: none;
    color: var(--color-green) !important;
  }

  .v-list-item--active .ui-select__item-checkbox,
  .v-list-item--active .ui-select__item-checkbox {
    .ui-select__item-checkbox-unchecked {
      display: none;
    }

    .ui-select__item-checkbox-checked {
      display: flex;
    }
  }

  .v-list-item-title {
    color: var(--color-graphite);
    font-size: 12px !important;
    height: 24px;
    line-height: 24px;
    position: relative;
    text-align: left;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .v-list-item--active .v-list-item-title,
  .v-list-item--active .v-list-item__content {
    color: var(--color-green);
  }

  .v-list-item--active > .v-list-item__overlay {
    opacity: 0 !important;
  }

  .v-list-item--disabled {
    opacity: 0.7;
  }

  .v-list-item:hover,
  .v-list-item--active,
  .v-list-item--highlighted,
  .v-list-item:focus-visible {
    background-color: var(--color-green10);
  }

  .v-list-item + .v-list-item {
    border-top: 1px solid var(--color-gray10);
  }
}

.ui-select--attach-select-top .ui-select__menu {
  max-width: 100%;
}

.ui-select--attach-select-bottom .ui-select__menu {
  max-width: 100%;
  left: 0 !important;
  top: 0 !important;
}
</style>
